import { useState } from "react";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Send, ShieldCheck, Sparkles, Workflow } from "lucide-react";
import { useBaseOwnerId, useModelCreate } from "@rhino-project/core/hooks";

type MatchResult = {
  id: string;
  idea: string;
  summary: string;
  needs: string[];
};

const buildMatchResult = (idea: string): Omit<MatchResult, "id" | "idea"> => {
  const lower = idea.toLowerCase();
  const needs: string[] = [];
  const sectors: string[] = [];

  if (lower.includes("drone") || lower.includes("uav") || lower.includes("autonomy")) {
    needs.push("Arctic and remote-domain autonomous surveillance support");
    sectors.push("autonomous systems");
  }
  if (lower.includes("cyber") || lower.includes("secure") || lower.includes("encryption")) {
    needs.push("Cyber resilience and secure communications for deployed teams");
    sectors.push("cyber resilience");
  }
  if (lower.includes("sensor") || lower.includes("radar") || lower.includes("imaging")) {
    needs.push("Persistent sensing and ISR enhancement for joint operations");
    sectors.push("ISR and persistent sensing");
  }
  if (lower.includes("ai") || lower.includes("machine learning") || lower.includes("decision")) {
    needs.push("Decision-support tooling to improve readiness and response timelines");
    sectors.push("AI-enabled decision support");
  }
  if (lower.includes("energy") || lower.includes("battery") || lower.includes("power") || lower.includes("sustain")) {
    needs.push("Operational sustainment modernization for Canadian Defence mission environments");
    sectors.push("resilient sustainment");
  }
  if (lower.includes("quantum") || lower.includes("space") || lower.includes("satellite")) {
    needs.push("Strategic-sector capability mapping for sovereign technology advantage");
    sectors.push("strategic sovereign capability");
  }
  if (needs.length === 0) {
    needs.push(
      "Operational sustainment modernization for Canadian Defence mission environments",
      "Decision-support tooling to improve readiness and response timelines"
    );
    sectors.push("mission support", "readiness modernization");
  }

  return {
    summary: `Your solution appears to offer dual-use capability with defence relevance in ${sectors.slice(0, 2).join(" and ")}. This signal can be routed for navigator review and sponsor triage while preserving the original commercial framing.`,
    needs,
  };
};

export default function Landing() {
  const [companyInput, setCompanyInput] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedSummary, setTranslatedSummary] = useState("");
  const [matchedNeeds, setMatchedNeeds] = useState<string[]>([]);
  const [matchHistory, setMatchHistory] = useState<MatchResult[]>([]);
  const organizationId = useBaseOwnerId();
  const { mutate: createSubmission } = useModelCreate("submission" as any);

  const handleTranslateAndMatch = () => {
    const idea = companyInput.trim();
    if (!idea || isTranslating) return;

    setIsTranslating(true);

    // Persist the signal opportunistically, but keep the chat responsive even if
    // the public landing visitor is not signed in or the API rejects the write.
    try {
      createSubmission({
        organization_id: Number.isFinite(organizationId) ? organizationId : undefined,
        company_name: "Dual-use prospect",
        product_description: idea,
        status: "translated",
      } as any);
    } catch {
      // The deterministic match below is the user-visible fallback for demo use.
    }

    window.setTimeout(() => {
      const result = buildMatchResult(idea);
      const historyEntry = { id: `${Date.now()}-${idea.slice(0, 12)}`, idea, ...result };

      setTranslatedSummary(result.summary);
      setMatchedNeeds(result.needs);
      setMatchHistory((current) => [historyEntry, ...current].slice(0, 5));
      setCompanyInput("");
      setIsTranslating(false);
    }, 500);
  };

  return (
    <div className="relative w-full min-w-0 overflow-x-hidden bg-transparent text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[34rem] bg-[radial-gradient(circle_at_18%_18%,hsl(var(--accent)/0.20),transparent_30%),radial-gradient(circle_at_82%_8%,hsl(var(--primary)/0.18),transparent_28%)]" />
      <div className="relative z-10 mx-auto flex w-full max-w-7xl min-w-0 flex-col gap-10 py-2">
        <section className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-primary via-primary to-accent text-primary-foreground shadow-2xl shadow-primary/25">
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary-foreground/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 left-1/3 h-72 w-72 rounded-full bg-accent/25 blur-3xl" />
            <CardHeader className="relative space-y-4">
              <Badge className="w-fit bg-primary-foreground/20 text-primary-foreground shadow-sm">The Third Door</Badge>
              <CardTitle className="text-3xl leading-tight md:text-5xl">The Dual-Use On-Ramp for Canadian Defence Innovation</CardTitle>
              <CardDescription className="max-w-2xl text-base text-primary-foreground/90">
                Focus your submission through guided intake, capability translation, and trusted routing into Canadian Defence needs.
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-primary-foreground text-primary shadow-lg hover:bg-primary-foreground/90">
                  <Link to="/dual-use-on-ramp">Start Dual-Use On-Ramp</Link>
                </Button>
                <Button asChild variant="outline" className="border-primary-foreground/40 bg-primary-foreground/5 text-primary-foreground shadow-sm hover:bg-primary-foreground/10">
                  <Link to="/dual-use-on-ramp/intake">Go to Intake</Link>
                </Button>
                <Button asChild variant="secondary" className="shadow-sm">
                  <Link to="/dnd-showcase">View DND Team Showcase</Link>
                </Button>
                <Button asChild variant="secondary" className="shadow-sm">
                  <Link to="/defence-prime-path">Explore Defence Prime Path</Link>
                </Button>
              </div>
              <p className="max-w-3xl rounded-xl border border-primary-foreground/15 bg-primary-foreground/10 p-3 text-sm text-primary-foreground/90 shadow-inner">
                Commercial companies should choose the clearly labeled Dual-Use On-Ramp; traditional Defence Prime organizations can review the separate prime path without crossing journeys.
              </p>
            </CardContent>
          </Card>

          <Card className="group relative min-h-80 overflow-hidden border-white/70 bg-gradient-to-br from-primary/20 via-background to-accent/15 shadow-2xl shadow-primary/20">
            <img
              src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1600&q=85"
              alt="Pristine Canadian wilderness with mountains, evergreen forest, and a clear alpine lake"
              className="h-full min-h-80 w-full object-cover transition duration-700 group-hover:scale-[1.03]"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-primary/5 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/25 bg-white/15 p-4 text-white shadow-2xl backdrop-blur-md">
              <p className="text-sm font-medium">Canadian wilderness focus</p>
              <p className="text-xs text-white/85">A pristine landscape backdrop for sovereign capability and northern readiness.</p>
            </div>
          </Card>
        </section>

        <section className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Main Engagement Tool</h2>
              <p className="text-sm text-muted-foreground">Submit as many different ideas as you like; each match is kept below for comparison.</p>
            </div>
            <Badge variant="outline" className="w-fit bg-card/80 shadow-sm">Sequential matching enabled</Badge>
          </div>
          <Card className="overflow-hidden border-border/80 bg-card/95 shadow-2xl shadow-primary/10 backdrop-blur">
            <CardHeader className="bg-gradient-to-r from-primary/5 via-accent/5 to-transparent">
              <Badge className="w-fit gap-1" variant="secondary"><Sparkles className="h-3.5 w-3.5" /> Dual-Use Match Chat</Badge>
              <CardTitle>Describe your possible solution</CardTitle>
              <CardDescription>
                Share your capability in plain language. We translate it into defence-relevant terms and suggest potential needs from the Canadian Defence community.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <Textarea
                value={companyInput}
                onChange={(event) => setCompanyInput(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                    event.preventDefault();
                    handleTranslateAndMatch();
                  }
                }}
                placeholder="Example: We build rugged AI-enabled sensor platforms for remote monitoring, low-bandwidth environments, and autonomous alerts..."
                className="min-h-32 bg-background/80 shadow-inner"
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={handleTranslateAndMatch} disabled={isTranslating || !companyInput.trim()} className="shadow-lg shadow-primary/20">
                  {isTranslating ? "Translating..." : "Translate & Match"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
                <Badge variant="outline" className="bg-background/80">Server-backed signal log</Badge>
                <span className="text-xs text-muted-foreground">Tip: press Ctrl/⌘ + Enter to submit another idea quickly.</span>
              </div>

              {translatedSummary ? (
                <div className="space-y-3 rounded-2xl border border-border/80 bg-gradient-to-br from-muted/70 to-card p-4 shadow-inner">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Latest translated defence framing:</span> {translatedSummary}
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Potential matched needs:</p>
                    <ul className="space-y-2">
                      {matchedNeeds.map((need, index) => (
                        <li key={`${need}-${index}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                          <span>{need}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}

              {matchHistory.length > 1 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">Recent idea matches</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {matchHistory.slice(1).map((match) => (
                      <div key={match.id} className="rounded-xl border border-border/70 bg-background/70 p-3 shadow-sm">
                        <p className="line-clamp-2 text-sm font-medium text-foreground">{match.idea}</p>
                        <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">{match.summary}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">What The Third Door Delivers</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { title: "Dual-use entry", text: "A guided path for innovators entering defence procurement.", icon: Workflow },
              { title: "Trusted triage", text: "Human-in-the-loop review with confidence indicators.", icon: ShieldCheck },
              { title: "Actionable routing", text: "Clear next steps to opportunities and navigator handoff.", icon: CheckCircle2 },
            ].map((item) => (
              <Card key={item.title} className="group border-border/80 bg-card/95 shadow-xl shadow-primary/5 transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10">
                <CardHeader>
                  <Badge variant="secondary" className="w-fit shadow-sm">Capability</Badge>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <span className="rounded-xl bg-primary/10 p-2 shadow-inner transition group-hover:bg-primary/15">
                      <item.icon className="h-4 w-4 text-primary" />
                    </span>
                    {item.title}
                  </CardTitle>
                  <CardDescription>{item.text}</CardDescription>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">Designed for applicants, navigators, and sponsors.</CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">Dual-Use On-Ramp Stages</h2>
          <Card className="border-border/80 bg-gradient-to-br from-muted/70 via-card to-accent/10 shadow-xl shadow-primary/5">
            <CardContent className="space-y-4 p-5">
              <Progress value={25} />
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {["Signal", "Translate", "Readiness", "Route"].map((stage, index) => (
                  <div key={stage} className="rounded-xl border border-border/80 bg-card/90 p-3 shadow-lg shadow-primary/5">
                    <Badge variant="outline" className="bg-background/70 shadow-sm">{index + 1}</Badge>
                    <p className="mt-2 text-sm font-medium text-foreground">{stage}</p>
                  </div>
                ))}
              </div>
              <Button asChild className="shadow-lg shadow-primary/15">
                <Link to="/dual-use-on-ramp/intake">
                  Start guided intake
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">Confidence & Trust Signals</h2>
          <Card className="border-border/80 bg-card/95 shadow-xl shadow-primary/5">
            <CardContent className="space-y-3 p-5">
              {[
                "Deterministic fallback translation",
                "Human-in-the-loop triage",
                "Server-side model proxy security",
              ].map((item) => (
                <Alert key={item} className="border-border/80 bg-muted/50 shadow-sm">
                  <Badge variant="secondary" className="mr-2 shadow-sm">Verified</Badge>
                  <AlertDescription className="inline text-sm text-foreground">{item}</AlertDescription>
                </Alert>
              ))}
            </CardContent>
          </Card>
        </section>

        <section>
          <Card className="overflow-hidden border-border/80 bg-gradient-to-br from-secondary via-card to-accent/15 text-secondary-foreground shadow-2xl shadow-primary/10">
            <CardContent className="flex flex-col items-start justify-between gap-4 p-6 md:flex-row md:items-center">
              <div>
                <h3 className="text-xl font-semibold">Ready to begin your dual-use intake?</h3>
                <p className="text-sm text-secondary-foreground/80">Create your account and submit an initial signal in minutes.</p>
              </div>
              <Button asChild>
                <Link to="/register">Start Dual-Use Intake</Link>
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}